import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import LoyaltyLockIcon from '~/icons/components/LoyaltyLockIcon/LoyaltyLockIcon';
import MysteryBoxDetailsModal from '~/pdp/components/MysteryBoxDetailsModal';
import OutfitSavingsBanner from '~/pdp/components/OutfitSavingsBanner';
import PlatinumUnlockedMessage from '~/pdp/components/PlatinumUnlockedMessage/PlatinumUnlockedMessage';
import BaseMarketingCallout from '~/pdp/components/ProductDetailMarketingCallout';
import useLoyaltyPricing from '~/pdp/utils/useLoyaltyPricing';
import useOutfitSavingsBanner from '~/pdp/utils/useOutfitSavingsBanner';
import PreorderTag from '~/shared/components/PreorderTag';
import ProductRatingContainer from '~/shared/components/ProductRatingContainer/ProductRatingContainer';
import { isPreorderEligibleBySelectedProduct } from '~/shared/utils/isPreorderEligibleBySelectedProduct';
import useEMPPricing from '~/shared/utils/useEMPPricing';
import {
  desktop,
  mobile,
  useProductDetailContext,
  useBreakpoint,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import ProductDetailPrice from '../ProductDetailPrice';

const DesktopHeaderWrapper = styled.div`
  ${({ theme }) => theme.pdpStyles.productNameMargin};
`;

const MobileHeaderWrapper = styled.div`
  grid-area: mobileHeaderArea;
  margin: 0;
  padding: 16px;
  width: 100%;
  display: block;
  z-index: 1;
`;

const CalloutWrapper = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;

  ${desktop`
    margin-bottom: 8px;
  `}

  ${({ theme }) => theme.pdpStyles.mobileHeaderCallout}
`;

const TitleWrapper = styled.div`
  ${({ theme }) => theme.pdpStyles.mobileHeaderTitle}
`;

const ProductNameWrapper = styled.div`
  display: inline-block;
  margin-bottom: 8px;
  position: relative;
`;

const ProductNameRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const ProductDetailMarketingCallout = styled(BaseMarketingCallout)``;

const StyledLoyaltyLockIcon = styled(LoyaltyLockIcon)`
  margin-left: -5px;
  margin-top: -2px;
  bottom: 0;
  top: auto;
`;

const TokenOnlyCallout = styled.div`
  ${({ theme }) => theme.memberCreditCallout.memberCreditCalloutCopy};

  ${mobile`
    padding: 0px 8px;
  `}
`;

const OutfitSavingsBannerWrapper = styled.div`
  margin-top: 24px;

  ${mobile`
    margin-bottom: 8px;
  `}
`;

const PriceWrapper = styled.div`
  ${mobile`
    padding: 16px 0;
  `};
`;

function PDPHeaderContent({
  detailsModal,
  isLocked,
  isPlatinumExclusive,
  product,
  productName,
  showProductRating,
  productRef,
  reviewSectionRef,
}) {
  const { isMobile } = useBreakpoint();
  const { selectionStateByProductIndex } = useProductDetailContext();
  const showPreorder = isPreorderEligibleBySelectedProduct(
    product,
    selectionStateByProductIndex
  );
  const showLockedIcon = isPlatinumExclusive && isLocked;
  const { isTokenOnly } = useEMPPricing(product);
  const { isLoyaltyItem } = useLoyaltyPricing(product);
  const showTokenPricing = isTokenOnly && !isLoyaltyItem;
  const { showSavingsBanner, outfitValue, creditCount, savingsAmount } =
    useOutfitSavingsBanner(product);
  const isMysteryBox = product?.isMysteryBundle;

  return (
    <>
      {isMobile && (
        <MobileHeaderWrapper>
          {isPlatinumExclusive && !isLocked && <PlatinumUnlockedMessage />}
          <TitleWrapper>
            <ProductNameRow>
              <ProductNameWrapper>
                {productName}
                {showLockedIcon && (
                  <StyledLoyaltyLockIcon width={32} height={32} color="black" />
                )}
              </ProductNameWrapper>
              {isMysteryBox && detailsModal && (
                <MysteryBoxDetailsModal detailsModal={detailsModal} />
              )}
            </ProductNameRow>
            {showProductRating && (
              <ProductRatingContainer
                productRef={productRef}
                reviewSectionRef={reviewSectionRef}
              />
            )}
          </TitleWrapper>
          <CalloutWrapper>
            {showPreorder && (
              <PreorderTag dataAutotag="Pre-Order_Product_Title" />
            )}
            <ProductDetailMarketingCallout />
            {showTokenPricing && (
              <TokenOnlyCallout tabIndex={0}>
                <FormattedMessage
                  id="site_pdp.pdp_token_only_callout"
                  defaultMessage="VIP Credit Only"
                />
              </TokenOnlyCallout>
            )}
          </CalloutWrapper>
          {isMysteryBox && (
            <PriceWrapper>
              <ProductDetailPrice />
            </PriceWrapper>
          )}
          {showSavingsBanner && (
            <OutfitSavingsBannerWrapper>
              <OutfitSavingsBanner
                outfitValue={outfitValue}
                creditCount={creditCount}
                savingsAmount={savingsAmount}
              />
            </OutfitSavingsBannerWrapper>
          )}
        </MobileHeaderWrapper>
      )}
      {!isMobile && (
        <DesktopHeaderWrapper>
          <ProductNameRow>
            <ProductNameWrapper>
              {productName}
              {showLockedIcon && (
                <StyledLoyaltyLockIcon width={32} height={32} color="black" />
              )}
            </ProductNameWrapper>
            {detailsModal && (
              <MysteryBoxDetailsModal detailsModal={detailsModal} />
            )}
          </ProductNameRow>
          {showPreorder && (
            <PreorderTag dataAutotag="Pre-Order_Product_Title" />
          )}
          {showProductRating && (
            <ProductRatingContainer
              productRef={productRef}
              reviewSectionRef={reviewSectionRef}
            />
          )}
          <CalloutWrapper>
            <ProductDetailMarketingCallout />
            {showTokenPricing && (
              <TokenOnlyCallout tabIndex={0}>
                <FormattedMessage
                  id="site_pdp.pdp_token_only_callout"
                  defaultMessage="VIP Credit Only"
                />
              </TokenOnlyCallout>
            )}
          </CalloutWrapper>
        </DesktopHeaderWrapper>
      )}
    </>
  );
}

PDPHeaderContent.propTypes = {
  detailsModal: PropTypes.object,
  isLocked: PropTypes.bool,
  isPlatinumExclusive: PropTypes.bool,
  product: PropTypes.object,
  productName: PropTypes.element,
  productRef: PropTypes.object,
  reviewSectionRef: PropTypes.object,
  showProductRating: PropTypes.bool,
};

export default PDPHeaderContent;
